import { A } from '@ember/array';
import { debug, assert } from '@ember/debug';
import EmberObject from '@ember/object';
import { readOnly, equal } from '@ember/object/computed';
import Evented from '@ember/object/evented';
import { next } from '@ember/runloop';
import Service, { inject as service } from '@ember/service';
import { isNone } from '@ember/utils';
import { tracked } from '@glimmer/tracking';
import classic from 'ember-classic-decorator';
import { didCancel } from 'ember-concurrency';

import { UploadCredentialsError } from 'later/errors/upload-credentials';
import { ErrorSeverity } from 'later/services/errors';

import type { AbortMultipartUploadCommandOutput, CompleteMultipartUploadCommandOutput } from '@aws-sdk/client-s3';
import type NativeArray from '@ember/array/-private/native-array';
import type IntlService from 'ember-intl/services/intl';
import type MediaItemModel from 'later/models/media-item';
import type AlertsService from 'later/services/alerts';
import type ErrorsService from 'later/services/errors';
import type LaterConfigService from 'later/services/later-config';
import type LocalStorageManagerService from 'later/services/local-storage-manager';
import type MediaUploadService from 'later/services/media-upload';
import type UploadCredentialsService from 'later/services/upload-credentials';
import type { UploadTarget } from 'later/services/upload-credentials';
import type { UntypedService } from 'shared/types';
import type { JsonObject } from 'type-fest';

type S3UploadResult = CompleteMultipartUploadCommandOutput | AbortMultipartUploadCommandOutput;
type Uploader = 'sts' | 'cognito';

// hard coding 'later-incoming' for now, until mobile ready -iMack, Aug 16, 2017
const COGNITO_TARGET: UploadTarget = { bucket: 'later-incoming', keyPrefix: '' };

/**
 * Transient network failures that occur mid-upload (offline, dropped connection, aborted request)
 * rather than application bugs. Includes AWS SDK error names and browser `fetch` failure messages
 * (Chrome "Failed to fetch", Firefox "NetworkError when attempting to fetch resource", Safari "Load failed").
 */
export const isTransientNetworkError = (error: Error): boolean => {
  const transientErrorNames = ['NetworkingError', 'TimeoutError', 'RequestTimeTooSkewed'];
  // Browser fetch network failures are TypeErrors whose whole message is engine-specific.
  const transientFetchMessages = /^(failed to fetch|networkerror when attempting to fetch resource\.?|load failed)$/i;

  if (transientErrorNames.includes(error.name)) {
    return true;
  }

  return error instanceof TypeError && transientFetchMessages.test(error.message);
};

/**
 * This service queues media files for upload into AWS S3
 */
export default class UploadBusService extends Service.extend(Evented) {
  @service declare intl: IntlService;
  @service declare localStorageManager: LocalStorageManagerService;
  @service declare errors: ErrorsService;
  @service declare alerts: AlertsService;
  @service declare laterConfig: LaterConfigService;
  @service declare uploadCredentials: UploadCredentialsService;
  @service('media-upload') declare mediaUploadService: MediaUploadService;

  /**
   * Queue of File objects to be uploaded
   */
  @tracked queue: NativeArray<MediaUpload> = A([]);

  @equal('queue.length', 0) declare isEmpty: boolean;

  addUploadToQueue(
    file: Blob | File,
    mediaItem: MediaItemModel,
    successAction?: () => void,
    failureAction?: (error: Error | string) => void
  ): void {
    if (this.laterConfig.imageUploadAvailable) {
      mediaItem.isUploading = true;
      this.#addUploadToQueue(file, mediaItem, successAction, failureAction);
    } else {
      this.alerts.clear();

      this.alerts.warning(this.intl.t('alerts.media_items.new.image_upload_not_available.message'), {
        title: this.intl.t('alerts.media_items.new.image_upload_not_available.title')
      });
    }
  }

  clearUploadQueue(): void {
    // eslint-disable-next-line @typescript-eslint/no-empty-function -- we need to pass a function to next()
    next(this, () => {});
  }

  #addUploadToQueue(
    file: Blob | File,
    mediaItem: MediaItemModel,
    successAction?: () => void,
    failureAction?: (error: Error | string) => void
  ): void {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const self = this;
    const uploads = this.queue;
    const upload = this.#createNewMediaUpload(this, file, mediaItem);

    upload.on('didBeginUpload', () => this.trigger('didBeginUpload'));

    upload.on('didFinishUpload', function () {
      debug('Got didFinishUpload');

      mediaItem.isUploading = false;

      next(self, () => {
        if (!self.isEmpty) {
          const nextObj = uploads.firstObject;
          nextObj?.call(self.errors, self.mediaUploadService);
        }
      });
    });

    upload.on('uploadSuccess', () => {
      if (typeof successAction === 'function') {
        //in case it's undefined
        successAction();
      }

      self.trigger('uploadSuccess');
    });

    upload.on('uploadFailure', (reason: Error) => {
      if (typeof failureAction === 'function') {
        //in case it's undefined
        failureAction(reason);
      }
    });

    if (self.isEmpty) {
      upload.call(this.errors, this.mediaUploadService);
    }

    uploads.pushObject(upload);
  }

  #createNewMediaUpload(service: UploadBusService, file: Blob | File, mediaItem: MediaItemModel): MediaUpload {
    assert('Must pass a valid flash service', service);
    assert('Must pass a valid file', file);
    assert('Must pass a valid mediaItem', mediaItem);

    if (!isNone(file) && file.type.match(/video.*/)) {
      mediaItem.mediaType = 'video';
      mediaItem.processing = true;
    } else {
      mediaItem.mediaType = 'image';
    }

    return MediaUpload.create({
      uploadBusService: service,
      localStorageManager: this.localStorageManager,
      errors: this.errors,
      uploadCredentials: this.uploadCredentials,
      file,
      mediaItem
    });
  }
}

/**
 * Wrapper for individual media item upload actions.
 * When created, it handles media item states during upload to S3
 */
@classic
class MediaUpload extends EmberObject.extend(Evented) {
  @readOnly('uploadBusService.queue') declare queue: NativeArray<MediaUpload>;

  @tracked uploadId = null;

  @tracked declare mediaUploadService: UntypedService;
  @tracked declare uploadBusService: UploadBusService;
  @tracked declare localStorageManager: LocalStorageManagerService;
  @tracked declare errors: ErrorsService;
  @tracked declare uploadCredentials: UploadCredentialsService;
  /** Chosen as the upload starts; null on a failure before the choice, so telemetry never credits a default */
  @tracked uploader: Uploader | null = null;
  @tracked declare file: Blob | File;
  @tracked declare mediaItem: MediaItemModel;

  /**
   * @see {@link http://docs.aws.amazon.com/AWSJavaScriptSDK/latest/AWS/S3.html}
   */
  async call(errors: ErrorsService, mediaUploadService: UntypedService): Promise<void> {
    // Note: we can't inject this into an Ember Object, so need to pass it -iMack June 2019
    this.errors = errors;
    this.mediaUploadService = mediaUploadService;
    this._uploadToS3();
  }

  /**
   * Uses AWS SDK V3 modular packages to setup S3 client with credentials.
   * Upload is handled by aws-sdk/lib-storage in order to track httpUploadProgress
   */
  async _uploadToS3(): Promise<void> {
    this.mediaItem.uploadProgress = 0;
    this.mediaItem.originalFilename = this.file.name;
    this.trigger('didBeginUpload');

    try {
      this.uploader = await this.#selectUploader();
      const data = await this.#upload();
      if (!this.mediaItem.get('isVideo') && 'Location' in data && data.Location) {
        this.mediaItem.imageUrl = data.Location;
      }
      if (!this.mediaItem.uploadProgress) {
        await this.mediaItem.destroyRecord();
        this.#reportFailure(new Error('_uploadToS3: Media failed to upload'));
        return;
      }
      await this.mediaItem.save();
    } catch (error: unknown) {
      if (error instanceof Error && !didCancel(error)) {
        this.#reportFailure(error);
      }
      return;
    }

    this.errors.log('Media upload succeeded', this.#buildTelemetry(), ErrorSeverity.Info);
    this.success();
  }

  // The backend validates against the destination group's owning account, which differs from the
  // user's own for contributors. Read as the upload starts so a flag flipped while it was queued applies,
  // and load the account if the store does not hold it yet so a canary account is never signed with Cognito.
  async #selectUploader(): Promise<Uploader> {
    const account = await (await this.mediaItem.group)?.account;
    return account?.rolloutStsUploads ? 'sts' : 'cognito';
  }

  async #upload(): Promise<S3UploadResult> {
    const { bucket, keyPrefix, clientOptions } =
      this.uploader === 'sts'
        ? await this.uploadCredentials.fetchTarget({
            kind: 'media_item',
            group_id: this.mediaItem.belongsTo('group').id()
          })
        : COGNITO_TARGET;
    this.mediaItem.processingBucket = bucket;
    this.mediaItem.processingKey = this.mediaUploadService.generateProcessingKey(
      this.file,
      this.mediaItem.get('isVideo'),
      keyPrefix
    );

    return this.mediaUploadService.uploadToS3.perform(
      this.file,
      bucket,
      this.mediaItem.processingKey,
      this.setUploadProgress.bind(this),
      clientOptions
    );
  }

  /** Logs the failure, then releases the queue through `failure` */
  #reportFailure(error: Error): void {
    this.#logFailure(error);
    this.failure(error);
  }

  #logFailure(error: Error): void {
    const credentialFields: JsonObject =
      error instanceof UploadCredentialsError
        ? { credentialsReason: error.reason, credentialsStatus: error.status ?? null }
        : {};
    const isExpected = isTransientNetworkError(error) || (error instanceof UploadCredentialsError && error.isTransient);

    const fields = { ...this.#buildTelemetry(), ...credentialFields, fileName: this.file.name };

    this.errors.log(error, fields, isExpected ? ErrorSeverity.Info : ErrorSeverity.Error);
    if (!isExpected) {
      // Error severity goes to RUM, Info to Logs, and the two are sampled differently in production:
      // an Info row per failure keeps every outcome in the sink the success rows use.
      this.errors.log('Media upload failed', { ...fields, error: error.message }, ErrorSeverity.Info);
    }
  }

  /** Fields the canary dashboard splits upload outcomes by */
  #buildTelemetry(): JsonObject {
    return {
      uploader: this.uploader,
      processingBucket: this.mediaItem.processingBucket,
      processingKey: this.mediaItem.processingKey,
      fileType: this.file.type,
      fileSize: this.file.size
    };
  }

  setUploadProgress(progress: number): void {
    this.mediaItem.uploadProgress = progress;
  }

  success(): void {
    if (this.queue) {
      this.queue.removeObject(this);
    }

    this.trigger('didFinishUpload');
    this.trigger('uploadSuccess');
  }

  failure(reason: Error): void {
    if (this.queue) {
      this.queue.removeObject(this);
    }

    this.trigger('didFinishUpload');
    this.trigger('uploadFailure', reason);
  }
}

declare module '@ember/service' {
  interface Registry {
    'upload-bus': UploadBusService;
  }
}
